// ============================================================================
// Canonical Pig Dailys route contracts — the hub AND its daily record
// ============================================================================
//
// Both surfaces under PigDailysRouter are written in ONE module, for the reason
// `layerDailysPaths.ts` states: `{view: 'pigdailys', paths: ['/pig/dailys/*']}`
// is a single splat row, and the router picks between exactly two shapes on the
// RAW pathname alone:
//
//   /pig/dailys        -> pig.dailys.index   (PigDailysHub)
//   /pig/dailys/       -> pig.dailys.index   (empty suffix, hub again)
//   /pig/dailys/<id>   -> pig.dailys.detail  (PigDailyPage)
//
// The router's own expression is
// `pathname.startsWith('/pig/dailys/') ? pathname.slice(len) || null : null`
// (PigDailysView.tsx), so the index predicate's correctness is a statement
// about what the record predicate does NOT claim, and vice versa. Splitting
// them across two modules would mean writing an exclusion in one file that only
// the other file's contents make true.
//
// Neither predicate knows about the ledger, the renderer, or any data owner.
//
// RESERVED SEGMENTS: there are NONE beneath `/pig/dailys/`. `src/lib/routes.ts`
// registers exactly one `/pig/dailys` path (`VIEW_TO_PATH.pigdailys`) and no
// `/pig/dailys/<segment>` route, alias source, alias target, or prefix alias —
// the pig domain appears in neither ALIASES_EXACT nor ALIASES_PREFIX. PIG_ROUTES
// declares no sibling namespace inside this splat either (`/pig/batches/*` and
// `/pig/sows/*` are separate rows, not descendants), and the router's id
// expression special-cases no value. That absence is derived, not asserted here:
// a future `/pig/dailys/archive` row would have to add a reserved set to this
// module AND appear in the static proof that pins the three facts above;
// nothing else would notice it.
// ============================================================================

import {isCanonicalCommentAnchorHash} from './commentAnchorHash.ts';

export const PIG_DAILYS_INDEX_PATHNAME = '/pig/dailys';
export const PIG_DAILYS_DETAIL_PATHNAME_PREFIX = '/pig/dailys/';

type LocationLike = Readonly<{pathname?: unknown; search?: unknown; hash?: unknown}>;

// One meaningful raw segment: no separator in either direction, no NUL, no line
// break, and no query/fragment marker smuggled into a bare pathname string.
function isSafeRawSegment(rawSegment: string): boolean {
  return !(
    rawSegment === '' ||
    rawSegment.includes('/') ||
    rawSegment.includes('\\') ||
    rawSegment.includes('\0') ||
    rawSegment.includes('\r') ||
    rawSegment.includes('\n') ||
    rawSegment.includes('?') ||
    rawSegment.includes('#')
  );
}

// The same rules again after ONE decode, which is what turns `%2F`, `%5C`,
// `%00`, `%0A`, `%0D`, `%2E`, and `%2E%2E` into rejections rather than into
// accepted opaque text. A malformed escape makes decodeURIComponent throw and
// the caller's catch returns false.
function isSafeDecodedSegment(segment: string): boolean {
  return (
    segment.trim() !== '' &&
    segment !== '.' &&
    segment !== '..' &&
    !segment.includes('/') &&
    !segment.includes('\\') &&
    !segment.includes('\0') &&
    !segment.includes('\r') &&
    !segment.includes('\n') &&
    !segment.includes('?') &&
    !segment.includes('#')
  );
}

function pathnameOf(location: unknown): unknown {
  if (typeof location === 'string') return location;
  if (!location || typeof location !== 'object' || Array.isArray(location)) return null;
  return (location as LocationLike).pathname;
}

/**
 * The bare hub path, and nothing else.
 *
 * `/pig/dailys/` is excluded deliberately: PigDailysRouter DOES render the hub
 * there (the suffix is empty, so `dailyId` is null), but the splat base is not
 * the audited direct-entry route, and the generic resolver classifies it as
 * this same index surface. Leaving it inside the release would hand a RELEASED
 * verdict to a raw shape no reviewer admitted; the disposition boundary
 * instead supplies explicit recovery.
 */
export function isCanonicalPigDailysIndexPathname(pathname: unknown): pathname is string {
  return pathname === PIG_DAILYS_INDEX_PATHNAME;
}

/**
 * `/pig/dailys/<id>` with exactly one decoded-safe segment.
 *
 * `/pig/dailys` does not start with the prefix and `/pig/dailys/` ends at it,
 * so this predicate cannot claim either hub form. The raw segment is checked
 * BEFORE the decoder so a literal separator, NUL, or line break never reaches
 * it, then the value is decoded once and re-checked so an encoded separator,
 * dot segment, NUL, or line break cannot widen the record release.
 */
export function isCanonicalPigDailysDetailPathname(pathname: unknown): pathname is string {
  if (typeof pathname !== 'string' || !pathname.startsWith(PIG_DAILYS_DETAIL_PATHNAME_PREFIX)) {
    return false;
  }
  const rawSegment = pathname.slice(PIG_DAILYS_DETAIL_PATHNAME_PREFIX.length);
  if (!isSafeRawSegment(rawSegment)) return false;
  try {
    return isSafeDecodedSegment(decodeURIComponent(rawSegment));
  } catch {
    return false;
  }
}

/**
 * The hub interprets NEITHER search nor hash. `PigDailysHub` calls no location
 * hook at all — the router reads `location.pathname` and hands the hub the
 * feeder directory — and its filter, group and saved-view state is component or
 * persisted state, never URL state. So a presentation-only query or fragment on
 * the canonical hub path does not change this claim, exactly as on the released
 * Cattle and Layer Dailys indexes.
 */
export function isCanonicalPigDailysIndexLocation(location: unknown): boolean {
  return isCanonicalPigDailysIndexPathname(pathnameOf(location));
}

/**
 * The record page is the opposite case, and the difference is real rather than
 * stylistic: `PigDailyPage` READS `location.hash` to scroll a deep-linked
 * `#comment-<id>` mention into view. A hash-carrying load is therefore a
 * materially different render than the audited one, so both search and hash
 * stay out of the released pair and receive explicit route recovery rather
 * than a broad loader fallback.
 *
 * A bare string is accepted only when it is plainly a pathname, so a caller or
 * test seam cannot smuggle an unsupported query or fragment into the target.
 */
export function isCanonicalPigDailysDetailLocation(location: unknown): boolean {
  if (typeof location === 'string') return isCanonicalPigDailysDetailPathname(location);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;

  const {pathname, search, hash} = location as LocationLike;
  return (
    (search === undefined || search === '') &&
    (hash === undefined || hash === '' || isCanonicalCommentAnchorHash(hash)) &&
    isCanonicalPigDailysDetailPathname(pathname)
  );
}
